import React, { useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { inventoryItemByAisleByStatus } from "../../../../graphql/queries";
import { updateInventoryItemMini } from "../../../../graphql/inventory/custom-mutations";
import { sanitizeInventoryItem } from "../../../../graphql/inventory/inventoryCRUD";

import { Backdrop, CircularProgress, makeStyles } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import classnames from "classnames";

import { Button, Confirm, SaveButton, Toolbar } from "react-admin";

import { deepRedirect } from "../../../../utils/utils";
import { logError } from "../../../../utils/appUtils";
import { InventoryStatus } from "../../../../utils/constants";
import {
  doUpdateLocation,
  getLocationFullName,
} from "../../../../utils/locationUtils";
import { handleDeleteAisle } from "./aisleUtils";
import { DeleteIcon } from "../../../../components/common/iconography";

const useStyles = makeStyles(
  (theme) => ({
    backdrop: {
      zIndex: theme.zIndex.modal + 1,
      color: "#fff",
    },
    deleteButton: {
      color: theme.palette.error.main,
      "&:hover": {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        // Reset on mouse devices
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
    },
  }),
  { name: "RaDeleteWithConfirmButton" },
);

const updateLocationsInventories = async (data) => {
  const promiseList = data.locations?.items.map((itm) => {
    itm.zoneId = data.zoneId;
    return doUpdateLocation(itm);
  });
  await Promise.all(promiseList);

  await processInventoriesUpdate(data);
};

const processInventoriesUpdate = async (aisle) => {
  // handle structured AISLE update
  const responseList = [];
  debugger;

  const statusQueries = [
    InventoryStatus.UNLOADED,
    InventoryStatus.REVIEW,
    InventoryStatus.STORED,
  ];

  for (const queryStatus of statusQueries) {
    let nextToken = true;
    const searchQuery = {
      aisleId: aisle.id,
      status: { beginsWith: queryStatus },
      limit: 1000,
    };

    while (nextToken) {
      const input =
        nextToken === true ? searchQuery : { nextToken, ...searchQuery };
      let results = await API.graphql(
        graphqlOperation(inventoryItemByAisleByStatus, input),
      );
      const inventoryItems =
        results.data.inventoryItemByAisleByStatus.items.filter(
          (item) => item.locationId,
        );
      nextToken = results.data.inventoryItemByAisleByStatus.nextToken;

      const responses = await Promise.allSettled(
        inventoryItems.map((inventoryItem) => {
          return doUpdateInventoryItem(inventoryItem, aisle);
        }),
      );
      responseList.push(...responses);
    }
  }

  const resError = responseList.find((item) => item.status === "rejected");
  if (resError) {
    logError(
      "reloadYard:AisleEdit::processInventoriesUpdate:response",
      resError,
      false,
    );
    throw resError;
  }
};

const doUpdateInventoryItem = (inventoryItem, aisle) => {
  const inventoryLocation = aisle.locations.items.find(
    (itm) => itm.id === inventoryItem.locationId,
  );
  inventoryLocation.aisle = aisle;
  inventoryLocation.zone = aisle.zone;

  let toUpdateInventoryItem = {
    ...inventoryItem,
    locationFullName: getLocationFullName(inventoryLocation),
    zoneId: aisle.zoneId,
  };

  toUpdateInventoryItem = sanitizeInventoryItem(toUpdateInventoryItem);

  return API.graphql(
    graphqlOperation(updateInventoryItemMini, {
      input: toUpdateInventoryItem,
    }),
  );
};

export const AisleEditToolbar = ({
  record,
  backPath,
  className,
  notify,
  history,
  ...props
}) => {
  const classes = useStyles();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [open, setOpen] = useState(false);

  const transform = async (data) => {
    data = { ...data };
    setIsSaving(true);

    delete data.area;
    delete data.zone;
    delete data.locations;
    delete data.tags;

    return data;
  };

  const onSuccess = async ({ data }) => {
    try {
      debugger;
      if (record.zoneId !== data.zoneId || record.name !== data.name) {
        await updateLocationsInventories(data);
      }

      notify("Aisle updated successfully", "success");
      deepRedirect(history, backPath);
    } catch (error) {
      logError("ReloadYard:AisleEdit:onSuccess", error, notify);
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError("ReloadYard:AisleEdit:onFailure", error, notify);
  };

  const handleDialogOpen = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);

  const handleDelete = async (event) => {
    try {
      setOpen(false);
      setIsDeleting(true);

      await handleDeleteAisle(record, notify);

      deepRedirect(history, backPath);
    } catch (error) {
      logError("reloadYard:AisleEdit:handleDelete", error, false);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Toolbar style={{ justifyContent: "space-between" }}>
      <Backdrop className={classes.backdrop} open={isSaving || isDeleting}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <SaveButton
        {...props}
        label="Save"
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
        disabled={props.pristine}
      />
      <Button
        {...props}
        label="Delete"
        disabled={isSaving || isDeleting}
        onClick={handleDialogOpen}
        className={classnames(
          "ra-delete-button",
          classes.deleteButton,
          className,
        )}
      >
        <DeleteIcon />
      </Button>
      <Confirm
        isOpen={open}
        title={"Delete Confirmation"}
        content={
          "Deleting an Aisle will delete all of its locations. Are you sure you want to delete this Aisle?"
        }
        onConfirm={handleDelete}
        onClose={handleDialogClose}
      />
    </Toolbar>
  );
};
